<template>
    <v-dialog v-model="internalShow" persistent width="500" overlay-opacity="0.59">
        <v-card :style="{ minHeight: successOrder || cancelledOrder ? '300px' : '600px' }" class="d-flex flex-column">
            <!-- Timer -->
            <div v-if="internalShow && !loadingOrder && !successOrder && !cancelledOrder" class="mb-4">
                <BaseTimer v-if="currentQuoteForTimer" :timeLimit="currentQuoteForTimer.expiration"
                    @timeDue="() => $emit('timeDue', currentQuoteForTimer.rfq_id)" />
            </div>

            <!-- Card title -->
            <v-card-title class="justify-center ">
                {{
                    loadingOrder
                        ? ($t('quoteInfo.loadingOrder') || '').toUpperCase()
                        : (cancelledOrder
                            ? ($t('quoteInfo.cancelledOrder') || '').toUpperCase()
                            : (successOrder
                                ? ''
                                : ($t('quoteInfo.confirmation') || '').toUpperCase()
                            )
                        )
                }}
            </v-card-title>

            <!-- Quote summary -->
            <v-card-text v-if="!loadingOrder && !successOrder && !cancelledOrder"
                class="d-flex flex-column justify-center">
                <slot name="summary-extra"></slot>

                <p>{{ $t('quoteInfo.side') }}: {{ side }}</p>
                <p>{{ $t('quoteInfo.ccy') }}: {{ ccy }}</p>
                <p v-if="!plexusStore.activeTab.includes('ndf')">{{ $t('quoteInfo.settlement') }}: {{ settlement ?
                    settlement?.toUpperCase() : '' }}</p>
                <!-- Use rfq_sett_brl as maturity (provisionally). -->
                <p v-if="plexusStore.activeTab.includes('ndf')">{{ $t('quoteInfo.maturity') }}: {{ plexusStore.maturityDate
                }}</p>
                <!-- Use rfq_sett_ccy as fixing date (provisionally). -->
                <p v-if="plexusStore.activeTab.includes('ndf')">{{ $t('quoteInfo.fixing') }}: D-1</p>
                <p>{{ $t('quoteInfo.amountCcy') }}: {{ amount }}</p>

                <v-divider class="my-4"></v-divider>

                <p>{{ $t('quoteInfo.lastUpdate') }}: {{ selectedQuote ? formatTimeAndDate(selectedQuote.rfq_quote_time)
                    : '-' }}</p>
                <p>{{ $t('quoteInfo.timeDue') }}: {{ quoteExpirationTime }}</p>
                <p>{{ $t('quoteInfo.bank') }}: {{ bankName }}</p>
                <p>{{ $t('quoteInfo.rate') }}:   {{ selectedQuote ? (selectedQuote.rfq_quote_px_client || selectedQuote.rfq_quote_px || '-') : '-' }}</p>
                <!--<p>{{ $t('quoteInfo.spreadAvg') }}:
          {{ selectedQuote ? (selectedQuote.rfq_quote_spread_avg || 50) : 50 }}
        </p>
        <p>{{ $t('quoteInfo.spreadPtax') }}:
          {{ selectedQuote ? (selectedQuote.rfq_quote_spread_ptax || '-') : '-' }}
        </p>-->
                <p :style="{ '--news-font-size': formatTitle }" class="font-title ">{{ $t('quoteInfo.total') }}: {{ $t('plexus.confirmation.currencyPrefix') }}
                    <span class="font-weight-bold">{{ convertTotal() }}</span>
                </p>
            </v-card-text>

            <!-- Progress Circular -->
            <v-card-text v-if="loadingOrder" class="d-flex flex-column align-center fill-height"
                style="justify-content: space-evenly; padding-top: 40%;">
                <v-progress-circular indeterminate color="primary" size="140"></v-progress-circular>
            </v-card-text>

            <!-- Success or Cancelled Order -->
            <v-card-text v-if="successOrder || cancelledOrder" class="d-flex flex-column align-center"
                style="padding: 40px 20px;">
                <div class="d-flex align-center justify-center" style="gap: 50px; width: 100%;">
                    <v-avatar size="90" color="primary" class="d-flex align-center justify-center">
                        <v-icon color="white" size="80">
                            {{ successOrder ? 'mdi-check' : 'mdi-close' }}
                        </v-icon>
                    </v-avatar>

                    <div class="text-center">
                        <h2 class="font-weight-bold text-h4">
                            {{
                                successOrder
                                    ? ($t('quoteInfo.succesDeal') || '').toUpperCase()
                                    : ($t('quoteInfo.cancelledOrder') || '').toUpperCase()
                            }}
                        </h2>
                    </div>
                </div>

                <div class="d-flex justify-between align-center">
                    <v-btn @click="$emit('close')" class="mt-10 mx-2 white--text rounded" elevation="0" tile outlined
                        small>
                        {{ $t('instructions.close') }}
                    </v-btn>

                </div>

                <slot name="success-extra"></slot>
            </v-card-text>

            <!-- Card buttons -->
            <v-card-actions v-if="!successOrder && !cancelledOrder && !loadingOrder">
                <v-spacer></v-spacer>

                <v-btn text @click="$emit('cancel')">
                    {{ $t('instructions.cancel') }}
                </v-btn>

                <!-- COMMENT SENDQUOTE BUTTON FOR NOW -->
                <!-- <v-btn
          v-if="showSendQuoteButton"
          color="orange"
          tile
          :loading="sendQuoteButtonLoading"
          :disabled="sendQuoteButtonDisabled"
          @click="$emit('sendQuote')"
        >
          {{ $t('broker.buttons.sendQuote') }}
        </v-btn> -->

                <v-btn :color="confirmButtonColor" tile :loading="loadingOrder"
                    :disabled="currentQuoteForTimer?.expired" @click="$emit('confirm')">
                    {{ $t(confirmButtonText) }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import BaseTimer from '@/components/common/BaseTimer.vue'
import { mapState, mapStores } from "pinia";
import {
    useSettingsStore,
} from "@/store/index"
import { usePlexusStore } from '@/store/plexusStore';

export default {
    name: 'QuoteConfirmationDialog',
    components: { BaseTimer },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        rfqList: {
            type: Array,
            default: null
        },
        loadingOrder: Boolean,
        successOrder: Boolean,
        cancelledOrder: Boolean,
        currentQuoteForTimer: Object,
        side: String,
        ccy: String,
        settlement: String,
        amount: [String, Number],
        selectedQuote: Object,
        quoteExpirationTime: String,
        // showSendQuoteButton: Boolean,
        // sendQuoteButtonLoading: Boolean,
        // sendQuoteButtonDisabled: Boolean,
        confirmButtonColor: {
            type: String,
            default: 'primary'
        },
        confirmButtonText: {
            type: String,
            default: 'instructions.confirm'
        },
        formatTimeAndDate: Function
    },
    data() {
        return {
            internalShow: this.value,
            rfq: {},
            fontSetting: useSettingsStore()
        }
    },
    watch: {
        selectedQuote: {
            handler() {
                this.updateCurrentRfq()
            },
            immediate: true
        },
        rfqList: {
            handler() {
                this.updateCurrentRfq()
            },
            deep: true
        },
        value(val) {
            this.internalShow = val
        },
        internalShow(val) {
            this.$emit('input', val)
        }
    },
    computed: {
        ...mapStores(usePlexusStore),
        ...mapState(useSettingsStore, [
            "isMockDataOn"
        ]),
        bankName() {
            return this.selectedQuote && this.isMockDataOn ? this.selectedQuote.counterparty_name :
                this.selectedQuote && !this.isMockDataOn ? this.selectedQuote.bank_name : '-'
        },
        formatTitle() {
            return `${this.fontSetting.getFontSize} * 13px`
        },
    },
    methods: {
        updateCurrentRfq() {
            if (!this.selectedQuote?.rfq_id || !this.rfqList) {
                this.rfq = {}
                return
            }
            this.rfq = this.rfqList.find(c => c.rfq_id === this.selectedQuote.rfq_id) || {}
        },
        close() {
            this.internalShow = false
        },
        uploadFiles() {
            this.$emit('upload-files', true)
            this.internalShow = false;
        },
        formatDate(dateString) {
            if (dateString) {
                const year = dateString.substring(0, 4);
                const month = dateString.substring(4, 6);
                const day = dateString.substring(6, 8);
                return `${year}-${month}-${day}`;
            }
            return dateString;
        },
        convertTotal() {
            if (!this.amount) return
            const numberAmount =
                typeof this.amount === 'string'
                    ? Number(this.amount.replace(/,/g, ""))
                    : this.amount

            const total =
                (this.selectedQuote?.rfq_quote_px || this.selectedQuote?.rfq_quote_px_client ) * numberAmount


            return total.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        }
    }
}
</script>
<style scoped></style>